import { useMemo } from 'react';

import { useClient } from 'urql';
import { fromPromise } from 'xstate';

import { Allergen } from '@sg/graphql-schema';
import {
  type DietaryPreferencesMachineImplementations,
  mergeRemoteAllergens,
} from '@sg/shared-fe-features-menu';

import {
  GetDietaryPreferencesWithAllergensDocument,
  type GetDietaryPreferencesWithAllergensQuery,
  type GetDietaryPreferencesWithAllergensQueryVariables,
  useUpdateDietaryPreferencesWithAllergensMutation,
} from './graphql/CustomerDietaryPreferences.generated';
import { getDietaryPreferencesList } from './helpers';

// ─────────────────────────────────────────────────────────────────────────────

export const useFetchDietaryPreferencesActor = () => {
  const client = useClient();
  const [, updatePreferencesWithAllergens] = useUpdateDietaryPreferencesWithAllergensMutation();

  return useMemo<FetchCustomerDietaryPreferences>(
    () =>
      fromPromise(async (args) => {
        const { input } = args;
        const { preferences: existingPreferences } = input;

        const query = client.query<
          GetDietaryPreferencesWithAllergensQuery,
          GetDietaryPreferencesWithAllergensQueryVariables
        >;

        const response = await query(
          GetDietaryPreferencesWithAllergensDocument,
          {},
          { requestPolicy: 'network-only' },
        ).toPromise();

        const preferencesData = response.data?.getPreferences;

        if (!preferencesData) {
          throw new Error('Failed to fetch customer dietary preferences');
        }

        if (!preferencesData?.hasSyncedRestrictions) {
          const { error } = await updatePreferencesWithAllergens({
            input: {
              allergens: getDietaryPreferencesList(existingPreferences) as Allergen[],
            },
          });

          if (error) {
            throw new Error('Failed to sync local dietary preferences to remote');
          }

          return existingPreferences;
        }

        return mergeRemoteAllergens(preferencesData?.allergens ?? []);
      }),
    [client.query, updatePreferencesWithAllergens],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type FetchCustomerDietaryPreferences = NonNullable<
  NonNullable<DietaryPreferencesMachineImplementations['actors']>['fetchCustomerDietaryPreferences']
>;
